import type {
  ApproveAndSendInviteReply,
  ApproveAndSendInviteRequest,
  BatchSendInviteCodesReply,
  BatchSendInviteCodesRequest,
  ConsumeInviteCodeReply,
  ConsumeInviteCodeRequest,
  CreateInviteCodeReply,
  CreateInviteCodeRequest,
  DeleteInviteCodeReply,
  DeleteInviteCodeRequest,
  GetInviteCodeReply,
  GrantBetaAccessReply,
  GrantBetaAccessRequest,
  ListBetaAccessReply,
  ListBetaAccessRequest,
  ListInviteApplicationsReply,
  ListInviteApplicationsRequest,
  ListInviteCodesReply,
  ListInviteCodesRequest,
  RevokeBetaAccessReply,
  RevokeBetaAccessRequest,
  SendInviteCodeReply,
  SendInviteCodeRequest,
} from '@/types';
import api from './api';

type AppScopedRequest<T> = T & { app_id: string };

type GetInviteCodeApiRequest =
  | { id: string; code?: never; app_id: string }
  | { id?: never; code: string; app_id: string };

export const inviteCodeApi = {
  // 创建邀请码
  createInviteCode: async (
    data: AppScopedRequest<CreateInviteCodeRequest>,
  ): Promise<CreateInviteCodeReply> => {
    return api.post('/api/v1/invite-codes', data);
  },

  // 获取单个邀请码
  getInviteCode: async (
    data: GetInviteCodeApiRequest,
  ): Promise<GetInviteCodeReply> => {
    // 根据传入的是 id 还是 code 选择不同的路径
    if ('id' in data && data.id) {
      return api.get(`/api/v1/invite-codes/${data.id}`, {
        params: { app_id: data.app_id },
      });
    }
    if ('code' in data && data.code) {
      return api.get('/api/v1/invite-codes/by-code', {
        params: { app_id: data.app_id, code: data.code },
      });
    }
    throw new Error('Either id or code must be provided');
  },

  // 删除邀请码
  deleteInviteCode: async (
    data: AppScopedRequest<DeleteInviteCodeRequest>,
  ): Promise<DeleteInviteCodeReply> => {
    return api.delete(`/api/v1/invite-codes/${data.id}`, {
      params: { app_id: data.app_id },
    });
  },

  // 列表查询邀请码
  listInviteCodes: async (
    params: AppScopedRequest<ListInviteCodesRequest>,
  ): Promise<ListInviteCodesReply> => {
    return api.get('/api/v1/invite-codes', { params });
  },

  // 消费邀请码
  consumeInviteCode: async (
    data: AppScopedRequest<ConsumeInviteCodeRequest>,
  ): Promise<ConsumeInviteCodeReply> => {
    return api.post('/api/v1/invite-codes/consume', data);
  },

  // 直接授予内测资格
  grantBetaAccess: async (
    data: AppScopedRequest<GrantBetaAccessRequest>,
  ): Promise<GrantBetaAccessReply> => {
    return api.post('/api/v1/beta-access/grant', data);
  },

  // 撤销内测资格
  revokeBetaAccess: async (
    data: AppScopedRequest<RevokeBetaAccessRequest>,
  ): Promise<RevokeBetaAccessReply> => {
    return api.post('/api/v1/beta-access/revoke', data);
  },

  // 列表查询内测资格
  listBetaAccesses: async (
    params: AppScopedRequest<ListBetaAccessRequest>,
  ): Promise<ListBetaAccessReply> => {
    return api.post('/api/v1/beta-access/list', params);
  },

  // 列表查询申请
  listInviteApplications: async (
    params: AppScopedRequest<ListInviteApplicationsRequest>,
  ): Promise<ListInviteApplicationsReply> => {
    return api.post('/api/v1/invite-applications/list', params);
  },

  // 审批并发送资格通知邮件
  approveAndSendInvite: async (
    data: AppScopedRequest<ApproveAndSendInviteRequest>,
  ): Promise<ApproveAndSendInviteReply> => {
    const { id, invite_code_id, app_id } = data;
    return api.post(
      `/api/v1/invite-applications/${id}/approve-and-send`,
      invite_code_id ? { app_id, invite_code_id } : { app_id },
    );
  },

  // 单条发送/重发邀请码到邮箱
  sendInviteCode: async (
    data: AppScopedRequest<SendInviteCodeRequest>,
  ): Promise<SendInviteCodeReply> => {
    return api.post(`/api/v1/invite-codes/${data.id}/send`, {
      to_email: data.to_email,
      app_id: data.app_id,
    });
  },

  // 批量发送：每个邮箱生成一个新邀请码并发送
  batchSendInviteCodes: async (
    data: AppScopedRequest<BatchSendInviteCodesRequest>,
  ): Promise<BatchSendInviteCodesReply> => {
    return api.post('/api/v1/invite-codes/batch-send', data);
  },
};
